/**
 * `market` namespace — English canonical source.
 *
 * Owns the `/market` overview route: headline indices, the SPDR sector heatmap,
 * the CNN Fear & Greed gauge, and the market-news panel. All figures are
 * display-only third-party data (Yahoo/CNN/RSS) — no money, no branded Decimal.
 * Keys are dot-free camelCase; `ko/market.ts` mirrors this shape (a missing key
 * is a compile error). Shared verbs (retry/loading) stay in `common.*`.
 */
export const market = {
  // ── route chrome ──
  eyebrow: "Market",
  title: "Market overview",
  description: "Headline indices, sector performance, the Fear & Greed index, and market news.",

  // ── index tiles ──
  indicesTitle: "Indices",
  indicesEmpty: "No index data available.",

  // ── Fear & Greed gauge ──
  fgiTitle: "Fear & Greed",
  fgiTrend: "90-day trend",
  fgiTrendEmpty: "Trend unavailable",
  fgiPrevClose: "Prev",
  fgi1Week: "1W",
  fgi1Month: "1M",
  fgi1Year: "1Y",
  fgiEmpty: "Fear & Greed unavailable.",

  // ── sector heatmap ──
  sectorsTitle: "Sectors",
  sectorsSubtitle: "S&P 500 sector ETFs · today",
  sectorsEmpty: "No sector data available.",

  // ── stock treemap (per-stock heatmap) ──
  heatViewSectors: "Sectors",
  heatViewStocks: "Stocks",
  stocksTitle: "Stocks",
  stocksSubtitle: "Top 200 by market cap · sized by weight",
  stocksAll: "All sectors",
  stocksBack: "← All sectors",
  stocksEmpty: "No stock data available.",
  stocksGateTitle: "Stocks",
  stocksGateBody: "Connect your Toss account to see the live per-stock heatmap.",
  stocksBaseline: "vs {date} close",
  stocksNoBaseline: "Change % appears after the next market close.",
  stocksSummaryAll: "{count} stocks across all sectors",
  stocksSummarySector: "{count} stocks in {sector}",
  legendNoData: "No data",

  // ── SPDR sector display names (treemap grouping / drill-down header) ──
  sectorXLK: "Technology",
  sectorXLF: "Financials",
  sectorXLE: "Energy",
  sectorXLV: "Health Care",
  sectorXLI: "Industrials",
  sectorXLY: "Consumer Discretionary",
  sectorXLP: "Consumer Staples",
  sectorXLU: "Utilities",
  sectorXLB: "Materials",
  sectorXLRE: "Real Estate",
  sectorXLC: "Communication Services",

  // ── news ──
  newsTitle: "Market news",
  newsEmpty: "No headlines right now.",

  // ── shared caption (interpolated) ──
  asOf: "as of {time}",
} as const;

export type MarketKey = keyof typeof market;
